import DOMPurify from "dompurify";

/**
 * Sanitize HTML content to prevent XSS attacks.
 * Use this whenever rendering user-generated content with v-html.
 */
export function sanitizeHtml(dirty: string | undefined | null): string {
  if (!dirty) return "";

  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      "p",
      "br",
      "b",
      "i",
      "em",
      "strong",
      "a",
      "ul",
      "ol",
      "li",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "blockquote",
      "span",
      "div",
    ],
    ALLOWED_ATTR: ["href", "target", "rel", "class"],
    ALLOW_DATA_ATTR: false,
  });
}

/**
 * Escape HTML entities for safe display as plain text.
 * Use this when you want to display text that might contain HTML as literal text.
 */
export function escapeHtml(text: string | undefined | null): string {
  if (!text) return "";

  const htmlEscapes: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };

  return text.replace(/[&<>"']/g, (char) => htmlEscapes[char]);
}

/**
 * Sanitize and highlight search terms in HTML content.
 * Safely highlights search terms without XSS risk.
 */
export function highlightSearchTerm(
  content: string,
  searchTerm: string
): string {
  if (!content) return "";
  if (!searchTerm) return sanitizeHtml(content);

  // First sanitize the content
  const sanitized = sanitizeHtml(content);

  // Escape special regex characters in search term
  const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Highlight matches (search term is escaped, so safe to use)
  return sanitized.replace(
    new RegExp(`(${escapedTerm})`, "gi"),
    '<mark class="bg-accent-200 text-accent-900 px-1 rounded">$1</mark>'
  );
}
